// SF3 — Cancellation Attribution and Reasons.
//
// Four buckets, and the fourth is not optional. A three-way split of buyer,
// seller and marketplace with no remainder is a split that is lying somewhere,
// so anything unattributable is shown rather than folded into one of the three.
//
// On this account the residual is everything, and that is a finding about the
// data rather than a hole in the page. The surface says what each actor's
// attribution would actually require, so the gap is actionable instead of
// merely blank.
//
// Confidence is displayed because the actors differ in kind. A buyer flag would
// be read straight from Amazon; seller and marketplace attribution has to be
// inferred from how an order's status changed. Direct and inferred must not
// look alike.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderFulfillment.module.css';
import { getCancellationAttribution } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

const CONFIDENCE_LABEL = {
  direct: 'read from Amazon',
  derived: 'inferred from status changes',
  none: 'no signal available',
};

const ACTOR_CLASS = {
  buyer: 'actorBuyer',
  seller: 'actorSeller',
  marketplace: 'actorMarketplace',
  unattributed: 'actorUnattributed',
};

const CancellationAttribution = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getCancellationAttribution(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const attribution = data?.attribution;
  const rows = attribution?.rows || [];
  const products = data?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Who cancelled, and why</h2>
        {attribution && (
          <span className={local.muted}>{count(attribution.total)} cancellations</span>
        )}
      </div>
      <p className={local.muted}>
        Buyer, you, or Amazon — plus everything that cannot be attributed, which
        is always shown rather than pushed into one of the other three.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load attribution'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && attribution && (
        <>
          {/* A residual this size is a data-quality signal, so it is stated
              plainly at the top rather than left for someone to infer from a
              row of zeros. */}
          {attribution.fully_unattributed && (
            <div className={`${local.banner} ${local.bannerWarn}`}>
              <FiAlertTriangle className={local.bannerIcon} />
              <div>
                <strong>None of these can be attributed yet</strong>
                <div>
                  Amazon returns no actor on the order, no cancellation reason,
                  and no buyer-cancelled flag on this account. The split below is
                  the shape this takes once a signal exists, not a measurement.
                </div>
              </div>
            </div>
          )}

          <div className={local.actorRow}>
            {rows.map((row) => (
              <div key={row.actor}
                className={`${local.actorTile} ${local[ACTOR_CLASS[row.actor]] || ''}`}>
                <div className={local.todayLabel}>{row.label}</div>
                <div className={local.todayValue}>{count(row.cancellations)}</div>
                <div className={local.todayFoot}>
                  {row.share === null || row.share === undefined
                    ? '—' : `${(row.share * 100).toFixed(0)}% · `}
                  <span className={local.confidenceTag}>
                    {CONFIDENCE_LABEL[row.confidence]}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {!data.reasons?.available && (
            <div className={local.provisional}>
              <strong>Reasons are not shown.</strong> {data.reasons?.reason} Your
              own reasons will never appear here even once the others do — Amazon
              accepts a reason code when you cancel and does not give it back, so
              that breakdown would have to be invented.
            </div>
          )}

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              By product
              <span className={local.groupCount}>
                {count(products.length)} with cancellations
              </span>
            </h3>
          </div>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th className={local.numeric}>Orders</th>
                  <th className={local.numeric}>Cancelled</th>
                  <th className={local.numeric}>Buyer</th>
                  <th className={local.numeric}>You</th>
                  <th className={local.numeric}>Amazon</th>
                  <th className={local.numeric}>Not attributable</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={7}>
                      No product with cancellations reached{' '}
                      {data.min_orders_for_ranking} orders in this period.
                    </td>
                  </tr>
                ) : products.map((row) => (
                  <tr key={row.asin}>
                    <td className={local.drillId}>{row.asin}</td>
                    <td className={local.numeric}>{count(row.orders)}</td>
                    <td className={local.numeric}>{count(row.cancelled)}</td>
                    <td className={local.numeric}>{count(row.buyer)}</td>
                    <td className={local.numeric}>{count(row.seller)}</td>
                    <td className={local.numeric}>{count(row.marketplace)}</td>
                    <td className={local.numeric}>{count(row.unattributed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CancellationAttribution;
